import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useTrackAddToCart from '../../hooks/tracking/useTrackAddToCart';
import useContext from '../../hooks/useContext';
import { getPreviewDate } from '../../preview/PreviewProvider';
import { get, post, put, _delete } from '../fetch';
import { createCartKey } from '../../providers/cart';
import { PostalContext } from '../../providers/postal';
import { QueryKey } from '../commerce/Delivery';
import useCommerceUrl from '../useCommerceUrl';

export const CART_SUMMARY_KEY = 'CART_SUMMARY_KEY';

export const getPreviewHeader = () => {
  const previewDate = getPreviewDate();

  if (!previewDate) {
    return undefined;
  }

  return [['X-Preview-Date', previewDate]];
};

export const useGetCart = (cartId: string | null) => {
  const queryClient = useQueryClient();
  const { commerceUrl } = useCommerceUrl();

  const {
    data,
    error,
    isLoading: loading,
  } = useQuery<Commerce.ICartResponse, Commerce.ICartError>(
    ['cart'],
    () => get<Commerce.ICartResponse>(`${commerceUrl}/cart/${cartId}`, getPreviewHeader()),
    {
      enabled: !!cartId,
      onSuccess: (data) => queryClient.setQueryData(CART_SUMMARY_KEY, data?.cart?.totals),
    },
  );

  return {
    cart: data?.cart,
    error,
    loading,
  };
};

export const useDeleteCartItem = (cartId: string | null) => {
  const queryClient = useQueryClient();
  const { postal } = useContext(PostalContext);
  const { commerceUrl } = useCommerceUrl();

  const {
    mutateAsync: deleteItem,
    isLoading: loading,
    error,
    reset,
  } = useMutation(
    (id: string) =>
      _delete<Commerce.ICartResponse>(`${commerceUrl}/cart/${cartId}/${id}`, getPreviewHeader()),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['cart'], data);
        queryClient.setQueryData(CART_SUMMARY_KEY, data?.cart?.totals);

        queryClient.invalidateQueries([QueryKey.DeliveryMethods, createCartKey(data.cart), postal]);
      },
    },
  );

  return {
    error,
    reset,
    deleteItem,
    loading,
  };
};

interface ISetCartQuantityVariables {
  quantity: number;
  variantId: string;
}

interface ISetCartQuantityRequest {
  quantity: number;
}

export const useSetCartItemQuantity = (cartId: string | null) => {
  const queryClient = useQueryClient();
  const { postal } = useContext(PostalContext);
  const { commerceUrl } = useCommerceUrl();

  const {
    mutateAsync: setQuantity,
    isLoading: loading,
    error,
    reset,
  } = useMutation<Commerce.ICartResponse, Commerce.CommerceException, ISetCartQuantityVariables>(
    ({ variantId, quantity }: ISetCartQuantityVariables) =>
      put<Commerce.ICartResponse, ISetCartQuantityRequest>(
        `${commerceUrl}/cart/${cartId}/${variantId}`,
        { quantity },
        getPreviewHeader(),
      ),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['cart'], data);
        queryClient.setQueryData(CART_SUMMARY_KEY, data?.cart?.totals);

        queryClient.invalidateQueries([QueryKey.DeliveryMethods, createCartKey(data.cart), postal]);
      },
    },
  );

  return {
    reset,
    error,
    setQuantity,
    loading,
  };
};

export const useAddCartCoupon = (cartId?: string) => {
  const queryClient = useQueryClient();
  const { commerceUrl } = useCommerceUrl();

  const {
    mutateAsync: addCoupon,
    isLoading: loading,
    reset,
    error,
  } = useMutation(
    (coupon: string) =>
      post<Commerce.ICartResponse, { coupon: string }>(
        `${commerceUrl}/cart/${cartId}/coupon`,
        { coupon },
        getPreviewHeader(),
      ),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['cart'], data);
        queryClient.setQueryData(CART_SUMMARY_KEY, data?.cart?.totals);
      },
    },
  );

  return {
    reset,
    error,
    addCoupon,
    loading,
  };
};

export const useDeleteCartCoupon = (cartId?: string) => {
  const queryClient = useQueryClient();
  const { commerceUrl } = useCommerceUrl();

  const {
    mutate,
    isLoading: loading,
    isError: error,
    reset,
  } = useMutation(
    () =>
      _delete<Commerce.ICartResponse>(`${commerceUrl}/cart/${cartId}/coupon`, getPreviewHeader()),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(['cart'], data);
        queryClient.setQueryData(CART_SUMMARY_KEY, data?.cart?.totals);
      },
    },
  );

  const deleteCoupon = useCallback(() => {
    mutate();
  }, []);

  return {
    deleteCoupon,
    error,
    loading,
    reset,
  };
};

export const useGetCartVouchers = (cartKey?: string, cartId?: string) => {
  const { commerceUrl } = useCommerceUrl();

  const { data, isLoading } = useQuery(
    ['cart', 'voucher', cartKey],
    () => get<IVoucher[]>(`${commerceUrl}/cart/${cartId}/vouchers`, getPreviewHeader()),
    {
      enabled: !!cartId && !!cartKey,
    },
  );

  return {
    vouchers: data,
    loading: isLoading,
  };
};

export interface IAddItemRequest {
  variantId: string;
  quantity?: number;
}

export const useAddToCart = (cartId: string | null) => {
  const queryClient = useQueryClient();
  const track = useTrackAddToCart();
  const { postal } = useContext(PostalContext);
  const { commerceUrl } = useCommerceUrl();

  const mutation = useMutation<Commerce.ICartResponse, Commerce.CommerceException, IAddItemRequest>(
    ({ variantId, quantity = 1 }: IAddItemRequest) => {
      return cartId
        ? post<Commerce.ICartResponse, IAddItemRequest>(
            `${commerceUrl}/cart/${cartId}`,
            { variantId, quantity: quantity },
            getPreviewHeader(),
          )
        : post<Commerce.ICartResponse, IAddItemRequest>(
            `${commerceUrl}/cart/`,
            { variantId, quantity: quantity },
            getPreviewHeader(),
          );
    },
    {
      onSuccess: (data, { variantId }: IAddItemRequest) => {
        const cartKey = createCartKey(data.cart);
        queryClient.setQueryData(['cart'], data);
        queryClient.setQueryData(CART_SUMMARY_KEY, data?.cart?.totals);
        track(variantId);
        queryClient.invalidateQueries([QueryKey.DeliveryMethods, cartKey, postal]);
      },
    },
  );

  return {
    reset: mutation.reset,
    error: mutation.error,
    addToCart: mutation.mutateAsync,
    loading: mutation.isLoading,
    addingId: mutation.variables,
  };
};
